import React, { useCallback, useEffect, useRef, useState } from "react";
import utils from "../../../utils";
import { useCountryFlag } from "../../../hooks/useCountryFlag";
import "./CountryCodeSelect.scss";
import "./CountryCodeSelect.global.scss";

export interface CountryCodeSelectProps {
  value?: string;
  onChange?: (countryId: string, phoneCode: string) => void;
  size?: "sm" | "md" | "lg";
  defaultOpen?: boolean;
}

const ArrowDownIcon: React.FC = () => (
  <svg
    className="country-code-select__arrow"
    viewBox="0 0 1024 1024"
    width="1em"
    height="1em"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M840.4 300H183.6c-19.7 0-30.7 20.8-18.5 35l328.4 380.8c9.4 10.9 27.5 10.9 37 0L858.9 335c12.2-14.2 1.2-35-18.5-35z" />
  </svg>
);

const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  size = "sm",
  defaultOpen = false,
}) => {
  const { getFlagSrcByCountryId } = useCountryFlag();
  const countries = window._config?.countryList || [];
  const currentCountryId = value || utils.getObject("countryId") || "PH";
  const currentCountry = countries.find(c => c.id === currentCountryId);
  const phoneCode = currentCountry?.phoneCode || "+63";
  const isSingleCountry = countries.length <= 1;

  const [open, setOpen] = useState(defaultOpen && !isSingleCountry);
  const rootRef = useRef<HTMLDivElement>(null);

  // 初始化时通知父组件当前的国家代码
  useEffect(() => {
    if (currentCountry && onChange && !value) {
      onChange(currentCountry.id, currentCountry.phoneCode);
    }
  }, [currentCountry, onChange, value]);

  // outside click 关闭
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    if (isSingleCountry) return;
    setOpen(prev => !prev);
  }, [isSingleCountry]);

  const handleSelect = useCallback(
    (countryId: string) => {
      const country = window._config?.countryList?.find(
        c => c.id === countryId
      );
      if (country && onChange) {
        onChange(countryId, country.phoneCode);
      }
      setOpen(false);
    },
    [onChange]
  );

  const rootClass = [
    "country-code-select",
    `country-code-select--${size}`,
    isSingleCountry ? "country-code-select--no-arrow" : "",
    open ? "country-code-select--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass} ref={rootRef}>
      <button
        type="button"
        className="country-code-select__trigger"
        onClick={handleToggle}
        disabled={isSingleCountry}
      >
        {(() => {
          const flagSrc = getFlagSrcByCountryId(currentCountryId);
          return flagSrc ? (
            <img
              src={flagSrc}
              alt=""
              className="country-code-select__flag"
              width={20}
              height={20}
              aria-hidden
            />
          ) : null;
        })()}
        <span className="country-code-select__phone-code">{phoneCode}</span>
        {!isSingleCountry && <ArrowDownIcon />}
      </button>
      {open && !isSingleCountry && (
        <div className="country-code-select__dropdown" role="listbox">
          {countries.map(item => (
            <div
              key={item.id}
              className={`country-code-select__option${
                item.id === currentCountryId
                  ? " country-code-select__option--selected"
                  : ""
              }`}
              role="option"
              aria-selected={item.id === currentCountryId}
              onClick={() => handleSelect(item.id)}
            >
              <span className="country-code-select__option-left">
                {(() => {
                  const flagSrc = getFlagSrcByCountryId(item.id);
                  return flagSrc ? (
                    <img
                      src={flagSrc}
                      alt=""
                      className="country-code-select__flag"
                      width={20}
                      height={20}
                      aria-hidden
                    />
                  ) : null;
                })()}
                <span className="country-code-select__option-name">
                  {item.displayName || item.name}
                </span>
              </span>
              <span className="country-code-select__option-code">
                {item.phoneCode}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
